import { Campaign, CampaignStatistics } from '@hprofits/api';
import { AdGroupId, TargetGroupId } from '@hprofits/shared';
import { computed, ComputedRef, Ref } from 'vue';

import {
  type AdGroupCopySource,
  useAdGroupCopyDialog,
} from '@/modules/campaigns/composables/actions/useAdGroupCopyDialog';
import { useAdGroupCreateDialog } from '@/modules/campaigns/composables/actions/useAdGroupCreateDialog';
import {
  CampaignEntityDeletionState,
  useCampaignEntityDeleteActions,
} from '@/modules/campaigns/composables/actions/useCampaignEntityDeleteActions';
import {
  type TargetGroupCopySource,
  useCopyTargetGroupAction,
} from '@/modules/campaigns/composables/actions/useCopyTargetGroupAction';
import { DataSource } from '@/modules/common/state/types';
import { UsePageControllerResult } from '@/modules/common/state/usePageController';

import { useCampaignDeleteAction } from '../actions/useCampaignDeleteAction';
import { useCreateTargetGroupAction } from '../actions/useCreateTargetGroupAction';
import { useCreativeTypeFlow, UseCreativeTypeFlowReturn } from '../actions/useCreativeTypeFlow';
import { CampaignGroupParams } from '../context/types';

type UseCampaignGroupContextActionsProps = Readonly<{
  params: CampaignGroupParams;
  campaignGroupSource: DataSource<Campaign>;
  campaignGroupStatisticsSource: DataSource<CampaignStatistics>;
  pageController: UsePageControllerResult;
  setSkipNextNavigationGuard: () => void;
}>;

type UseCampaignGroupContextActionsResult = Readonly<{
  isPending: ComputedRef<boolean>;
  // TODO: Do we really need promises here? They are button triggers and nobody awaits them
  deleteCampaignGroup: () => Promise<void>;
  createTargetGroup: () => Promise<void>;
  copyTargetGroup: (source: TargetGroupCopySource) => Promise<void>;
  createCreativeGroup: () => Promise<void>;
  createAdGroup: () => Promise<void>;
  copyAdGroup: (source: AdGroupCopySource) => Promise<void>;
  deleteTargetGroup: ReturnType<typeof useCampaignEntityDeleteActions>['deleteTargetGroup'];
  deleteCreativeGroup: ReturnType<typeof useCampaignEntityDeleteActions>['deleteCreativeGroup'];
  deleteAdGroup: ReturnType<typeof useCampaignEntityDeleteActions>['deleteAdGroup'];
  isCreatingTargetGroup: Readonly<Ref<boolean>>;
  copyingTargetGroupId: Readonly<Ref<TargetGroupId | null>>;
  copyingAdGroupId: Readonly<Ref<AdGroupId | null>>;
  entityDeletion: CampaignEntityDeletionState;
  creativeCreationState: Omit<UseCreativeTypeFlowReturn, 'openTypeDropdown'>; // TODO: Temporary hack until whole useCreativeTypeFlow will be rewritten
}>;

export const useCampaignGroupContextActions = ({
  params,
  campaignGroupSource,
  campaignGroupStatisticsSource,
  pageController,
  setSkipNextNavigationGuard,
}: UseCampaignGroupContextActionsProps): UseCampaignGroupContextActionsResult => {
  const data = computed(() => campaignGroupSource.state.data.value ?? null);

  const deleteCampaignAction = useCampaignDeleteAction({
    params,
    title: computed(() => data.value?.name ?? null),
    isLoading: campaignGroupSource.state.isLoading,
    pageController,
    allowNextNavigation: setSkipNextNavigationGuard,
  });

  const targetGroupNames = computed(() => data.value?.targetGroups?.map((tg) => tg.name) ?? []);

  const createTargetGroupAction = useCreateTargetGroupAction({
    campaignGroupId: params.campaignGroupId,
    targetGroupNames,
    pageController,
  });

  const copyTargetGroupAction = useCopyTargetGroupAction({
    campaignGroupId: params.campaignGroupId,
    targetGroupNames,
    pageController,
  });

  const { deleteTargetGroup, deleteCreativeGroup, deleteAdGroup, ...entityDeletion } = useCampaignEntityDeleteActions({
    campaignGroupId: params.campaignGroupId,
    targetGroups: computed(() => data.value?.targetGroups ?? []),
    creativeGroups: computed(() => data.value?.creativeGroups ?? []),
    adGroups: computed(() => data.value?.adGroups ?? []),
    campaignStatistics: campaignGroupStatisticsSource.state.data,
    pageController,
    allowNextNavigation: setSkipNextNavigationGuard,
  });

  // TODO: Update to normal creation flow via useCreativeGroupAction and a separate form dialog
  const { openTypeDropdown, ...creativeCreationState } = useCreativeTypeFlow({
    campaignGroupId: params.campaignGroupId,
    existingNames: computed(() => data.value?.creativeGroups?.map((cg) => cg.name) ?? []),
  });

  // TOOD: This action must use optimistic update - not returning creating state (check if there other as well)
  const { open } = useAdGroupCreateDialog({
    campaignGroupId: params.campaignGroupId,
    existingAdNames: computed(() => data.value?.adGroups?.map((ag) => ag.name) ?? []),
    targetOptions: computed(() => data.value?.targetGroups?.map((tg) => ({ value: tg.id, label: tg.name })) ?? []),
    creativeOptions: computed(() => data.value?.creativeGroups?.map((cg) => ({ value: cg.id, label: cg.name })) ?? []),
  });

  const copyAdGroupDialog = useAdGroupCopyDialog({
    campaignGroupId: params.campaignGroupId,
    existingAdNames: computed(() => data.value?.adGroups?.map((ag) => ag.name) ?? []),
    targetGroups: computed(() => data.value?.targetGroups ?? []),
    creativeOptions: computed(() => data.value?.creativeGroups?.map((cg) => ({ value: cg.id, label: cg.name })) ?? []),
    pageController,
  });

  const isPending = computed(
    () =>
      deleteCampaignAction.isDeletePending.value ||
      createTargetGroupAction.isCreatePending.value ||
      copyTargetGroupAction.copyingTargetGroupId.value !== null ||
      copyAdGroupDialog.copyingAdGroupId.value !== null ||
      entityDeletion.isPending.value ||
      creativeCreationState.isCreating.value,
  );

  return {
    isPending,
    deleteCampaignGroup: deleteCampaignAction.handleDelete,
    createTargetGroup: createTargetGroupAction.handleCreate,
    copyTargetGroup: copyTargetGroupAction.handleCopy,
    createCreativeGroup: async () => openTypeDropdown(),
    createAdGroup: async () => open(),
    copyAdGroup: copyAdGroupDialog.open,
    deleteTargetGroup,
    deleteCreativeGroup,
    deleteAdGroup,
    isCreatingTargetGroup: createTargetGroupAction.isCreatePending,
    copyingTargetGroupId: copyTargetGroupAction.copyingTargetGroupId,
    copyingAdGroupId: copyAdGroupDialog.copyingAdGroupId,
    entityDeletion,
    creativeCreationState,
  };
};
